/* eslint-disable react/jsx-key */
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import { TypeOption } from "components/app/BaseMultipleAutocomplete/BaseMultipleAutocomplete";
import { useRouter } from "next/router";
import { FC, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

interface ActivityLookupCompleteProps {
  category: "ACTIVITY_TYPE" | "ACTIVITY_PRODUCT" | "ACTIVITY_PURPOSE";
  field: ControllerRenderProps;
  fieldState: ControllerFieldState;
  label: string;
  ops: TypeOption[];
  fullWidth?: boolean;
  showMarginLeft?: boolean;
  hideTitle?: boolean;
  disabled?: boolean;
  limitTags?: number;
  handleChange?: (selected: { value: string; name: string }[]) => void;
}

const ActivityLookupMultipleComplete: FC<ActivityLookupCompleteProps> = ({
  field,
  fieldState,
  label,
  ops,
  fullWidth = false,
  showMarginLeft = false,
  hideTitle = false,
  disabled = false,
  limitTags = 1,
  handleChange,
}) => {
  const [value, setValue] = useState<TypeOption[]>(field.value);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(hideTitle ? true : false);
  const router = useRouter();

  const isPlaybook = router.pathname.includes("playbook");

  const onChange = (_e: any, value: TypeOption[]) => {
    setValue(value);
    field.onChange(value);
    handleChange?.(value);
  };

  const onFocus = () => {
    const filterValue = value?.filter((v) => ops.map((o) => o.value).includes(v.value));
    setValue(filterValue);
    field.onChange(filterValue);
    handleChange?.(filterValue);
  };

  const onOpen = () => setOpen(true);
  const onClose = (event: any) => {
    if (
      (event?.relatedTarget as HTMLElement)?.className?.includes?.("block-close") ||
      (event?.target as HTMLElement)?.parentElement?.className?.includes?.("block-close") ||
      (event?.target as HTMLElement)?.className?.includes?.("block-close")
    ) {
    } else {
      setOpen(false);
    }
  };

  return (
    <div
      style={{
        marginTop: hideTitle ? 3 : 8,
        flexBasis: fullWidth ? undefined : "49%",
        marginLeft: showMarginLeft ? "2%" : 0,
        width: fullWidth ? "100%" : undefined,
      }}
    >
      {label && (
        <p
          style={{
            fontSize: 12,
            color: "#535759",
            fontWeight: 500,
            display: "flex",
          }}
        >
          <span style={{ color: "#FF5F58", fontSize: "1rem", marginRight: 4 }}>*</span>
          {label}
        </p>
      )}
      <Autocomplete
        {...field}
        open={open}
        disableCloseOnSelect={true}
        onOpen={onOpen}
        onClose={onClose}
        getOptionLabel={(option) => option.value}
        loading={ops === undefined}
        blurOnSelect={false}
        clearOnBlur={false}
        multiple={true}
        limitTags={limitTags}
        size={"small"}
        disabled={disabled}
        isOptionEqualToValue={(o, v) => o.name === v.name}
        options={ops}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        renderOption={(props, option) => (
          <li {...props}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <p>{option.value}</p>
            </div>
          </li>
        )}
        renderTags={(value, getTagProps) => {
          return (
            <>
              {value.slice(0, limitTags).map((x, i) => {
                return (
                  <Chip
                    key={x.name}
                    {...(() => {
                      const { key, ...rest } = getTagProps({ index: i });
                      return rest;
                    })()}
                    label={x.value}
                    size={"small"}
                    style={
                      limitTags === 1
                        ? { maxWidth: 80, margin: "0 2px" }
                        : { maxWidth: 120, margin: "0 2px" }
                    }
                  />
                );
              })}

              {value.length - limitTags > 0 ? (
                <Tooltip
                  title={
                    <List
                      disablePadding={true}
                      dense={true}
                    >
                      {value.slice(limitTags).map((x, index) => (
                        <ListItem
                          dense={true}
                          key={x.value}
                        >
                          <ListItemText primary={x.value} />
                          <ListItemSecondaryAction style={{ right: 4 }}>
                            <IconButton
                              disabled={disabled}
                              size={"small"}
                              onClick={
                                disabled
                                  ? undefined
                                  : (
                                      getTagProps({
                                        index: index + limitTags,
                                      }) as any
                                    )?.onDelete
                              }
                            >
                              <Clear />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  }
                >
                  <Chip
                    size={"small"}
                    label={`${value.length - limitTags} more`}
                  />
                </Tooltip>
              ) : null}
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="normal"
            variant="outlined"
            classes={{ root: "dropdown-multiple-option" }}
            InputProps={{
              ...params.InputProps,
              type: "search",
              style: {
                width: showMarginLeft ? (hideTitle ? 192 : "100%") : "100%",
                marginTop: -15,
                background: "#fff",
                flexWrap: "nowrap",
                maxWidth: fullWidth ? undefined : isPlaybook ? 248.55 : 266.55,
              },
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            size={"small"}
            placeholder={field.value?.length ? "" : "Please select"}
          />
        )}
        onChange={onChange}
        onFocus={onFocus}
        value={field.value ?? value}
      />
    </div>
  );
};

export default ActivityLookupMultipleComplete;
